import React, { useState } from 'react';
import axios, { type AxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RelativeTime } from '@/components/RelativeTime';
import { AlertCircle, Copy, MoreHorizontal, Pencil, Plus, Trash2, Users, X } from 'lucide-react';
import { config } from '../../config';
import { useAuth } from '../../hooks/useAuth';
import { type Context, useContexts } from '../../hooks/useContexts';
import { useDivisions } from '../../hooks/useDivisions';
import { useTeams } from '../../hooks/useTeams';
import { useRoles } from '../../hooks/useRoles';
import {
  formatTeamWithRolesName,
  useAssignTeamRole,
  useContextTeams,
  useRemoveTeamRole,
} from '../../hooks/useContextTeams';
import ConfirmationModal from '../../components/ConfirmationModal';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AppSheet } from '@/components/ui/app-sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import { EmptyState } from '@/components/ui/empty-state';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const NO_CLONE = '__none__';

interface CreateContextRequest {
  name: string;
  organizationId?: string;
  divisionId?: string;
  cloneFromContextId?: string;
}

interface UpdateContextRequest {
  name?: string;
}

interface ApiErrorResponse {
  error: string;
  description?: string;
}

export default function Contexts() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organizationId = user?.organizationId;

  const [isCreateModalActive, setIsCreateModalActive] = useState(false);
  const [isEditModalActive, setIsEditModalActive] = useState(false);
  const [isManageTeamsModalActive, setIsManageTeamsModalActive] = useState(false);
  const [editingContext, setEditingContext] = useState<Context | null>(null);
  const [managingContext, setManagingContext] = useState<Context | null>(null);
  const [isDeleteConfirmActive, setIsDeleteConfirmActive] = useState(false);
  const [contextToDelete, setContextToDelete] = useState<Context | null>(null);

  const [formName, setFormName] = useState('');
  const [formLevel, setFormLevel] = useState<'organization' | 'division'>('organization');
  const [formDivisionId, setFormDivisionId] = useState('');
  const [formCloneFromId, setFormCloneFromId] = useState(NO_CLONE);

  const [editFormName, setEditFormName] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');

  const [submitError, setSubmitError] = useState<string | null>(null);

  const backendApi = config.backendApi;

  const { data: contexts, isLoading, isError, error } = useContexts(organizationId);
  const { data: divisions } = useDivisions(organizationId);
  const { data: teams } = useTeams(organizationId);
  const { data: roles } = useRoles(organizationId);
  const { data: contextTeams, isLoading: isContextTeamsLoading } =
    useContextTeams(managingContext?.id);

  const createContextMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    AxiosError<ApiErrorResponse>,
    CreateContextRequest
  >({
    mutationFn: async (newContext) => {
      await axios.post(`${backendApi}/api/contexts`, newContext, { withCredentials: true });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contexts'] });
      setIsCreateModalActive(false);
      resetCreateForm();
      setSubmitError(null);
    },
    onError: (err) => {
      setSubmitError(
        err.response?.data.description || err.response?.data.error || 'Failed to create context.',
      );
    },
  });

  const updateContextMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    AxiosError<ApiErrorResponse>,
    { id: string; data: UpdateContextRequest }
  >({
    mutationFn: async ({ id, data }) => {
      await axios.put(`${backendApi}/api/contexts/${id}`, data, { withCredentials: true });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contexts'] });
      void queryClient.invalidateQueries({ queryKey: ['user'] });
      setIsEditModalActive(false);
      setEditingContext(null);
      setEditFormName('');
      setSubmitError(null);
    },
    onError: (err) => {
      setSubmitError(
        err.response?.data.description || err.response?.data.error || 'Failed to update context.',
      );
    },
  });

  const deleteContextMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    AxiosError<ApiErrorResponse>,
    string
  >({
    mutationFn: async (id) => {
      await axios.delete(`${backendApi}/api/contexts/${id}`, { withCredentials: true });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contexts'] });
      void queryClient.invalidateQueries({ queryKey: ['user'] });
      setSubmitError(null);
    },
    onError: (err) => {
      setSubmitError(
        err.response?.data.description || err.response?.data.error || 'Failed to delete context.',
      );
    },
  });

  const assignTeamRoleMutation = useAssignTeamRole(managingContext?.id || '');
  const removeTeamRoleMutation = useRemoveTeamRole(managingContext?.id || '');

  const resetCreateForm = () => {
    setFormName('');
    setFormLevel('organization');
    setFormDivisionId('');
    setFormCloneFromId(NO_CLONE);
  };

  const handleCreateContext = (e: React.SubmitEvent) => {
    e.preventDefault();
    const payload: CreateContextRequest = { name: formName };
    if (formLevel === 'organization' && organizationId) {
      payload.organizationId = organizationId;
    } else if (formLevel === 'division' && formDivisionId) {
      payload.divisionId = formDivisionId;
    }
    if (formCloneFromId !== NO_CLONE) {
      payload.cloneFromContextId = formCloneFromId;
    }
    createContextMutation.mutate(payload);
  };

  const handleUpdateContext = (e: React.SubmitEvent) => {
    e.preventDefault();
    if (editingContext) {
      updateContextMutation.mutate({
        id: editingContext.id,
        data: { name: editFormName },
      });
    }
  };

  const handleDeleteContext = (context: Context) => {
    setContextToDelete(context);
    setIsDeleteConfirmActive(true);
  };

  const confirmDeleteContext = () => {
    if (contextToDelete) {
      deleteContextMutation.mutate(contextToDelete.id);
      setIsDeleteConfirmActive(false);
      setContextToDelete(null);
    }
  };

  const cancelDeleteContext = () => {
    setIsDeleteConfirmActive(false);
    setContextToDelete(null);
  };

  const openCreateModal = () => {
    resetCreateForm();
    setSubmitError(null);
    setIsCreateModalActive(true);
  };

  const openEditModal = (context: Context) => {
    setEditingContext(context);
    setEditFormName(context.name);
    setSubmitError(null);
    setIsEditModalActive(true);
  };

  const openCloneModal = (context: Context) => {
    setFormName(`${context.name} (Copy)`);
    setFormLevel(context.divisionId ? 'division' : 'organization');
    setFormDivisionId(context.divisionId || '');
    setFormCloneFromId(context.id);
    setSubmitError(null);
    setIsCreateModalActive(true);
  };

  const openManageTeamsModal = (context: Context) => {
    setManagingContext(context);
    setSelectedTeamId('');
    setSelectedRoleId('');
    setSubmitError(null);
    setIsManageTeamsModalActive(true);
  };

  const handleAssignTeamRole = (e: React.SubmitEvent) => {
    e.preventDefault();
    if (managingContext && selectedTeamId && selectedRoleId) {
      assignTeamRoleMutation.mutate(
        { teamId: selectedTeamId, roleId: selectedRoleId },
        {
          onSuccess: () => {
            setSelectedTeamId('');
            setSelectedRoleId('');
          },
          onError: (err) => {
            setSubmitError(err.message || 'Failed to assign role to team.');
          },
        },
      );
    }
  };

  const handleRemoveTeamRole = (teamId: string, roleId: string) => {
    if (managingContext) {
      removeTeamRoleMutation.mutate(
        { teamId, roleId },
        {
          onError: (err) => {
            setSubmitError(err.message || 'Failed to remove role from team.');
          },
        },
      );
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader title="Contexts" />
        <p className="text-sm text-muted-foreground">Loading contexts…</p>
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <PageHeader title="Contexts" />
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Error loading contexts: {error.message}</AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  const getDivisionName = (divisionId: string | null): string => {
    if (!divisionId) return 'Organization';
    const division = divisions?.find((d) => d.id === divisionId);
    return division ? division.name : 'Unknown Division';
  };

  const contextColumns: readonly ResponsiveTableColumn<Context>[] = [
    {
      key: 'name',
      label: 'Name',
      hideMobileLabel: true,
      render: (context) => <span className="font-medium">{context.name}</span>,
    },
    {
      key: 'level',
      label: 'Level',
      render: (context) => (
        <Badge
          className={
            context.divisionId
              ? 'bg-sev-5/15 text-sev-5 border-0 font-normal'
              : 'bg-sev-4/15 text-sev-4 border-0 font-normal'
          }
        >
          {getDivisionName(context.divisionId)}
        </Badge>
      ),
    },
    {
      key: 'created',
      label: 'Created',
      hideAt: 'mobile',
      render: (context) =>
        context.createdAt ? (
          <span className="text-xs text-muted-foreground">
            <RelativeTime value={context.createdAt} />
          </span>
        ) : null,
    },
    {
      key: 'updated',
      label: 'Updated',
      hideAt: 'mobile',
      render: (context) =>
        context.updatedAt ? (
          <span className="text-xs text-muted-foreground">
            <RelativeTime value={context.updatedAt} />
          </span>
        ) : null,
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Contexts"
        subtitle="Manage permission contexts for your organization"
        primary={
          <Button onClick={openCreateModal} className="gap-1.5">
            <Plus className="w-4 h-4" />
            Create context
          </Button>
        }
      />

      {submitError ? (
        <Alert variant="destructive" className="mb-4 relative">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{submitError}</AlertDescription>
          <button
            type="button"
            onClick={() => { setSubmitError(null); }}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Alert>
      ) : null}

      <ResponsiveTable<Context>
        columns={contextColumns}
        rows={contexts ?? []}
        rowKey={(c) => c.id}
        isLoading={isLoading}
        rowActions={(context) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Actions for ${context.name}`}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => { openManageTeamsModal(context); }}
                className="gap-2"
              >
                <Users className="w-4 h-4" />
                Manage teams
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => { openEditModal(context); }}
                className="gap-2"
                /* The organization's home context is where its owner's access
                   lives; the API refuses to rename or delete it, so offering the
                   control would only produce a 409. Clone stays available — it
                   reads the row rather than changing it. */
                disabled={context.isOrgHome === true}
              >
                <Pencil className="w-4 h-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => { openCloneModal(context); }}
                className="gap-2"
              >
                <Copy className="w-4 h-4" />
                Clone
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => { handleDeleteContext(context); }}
                className="gap-2 text-sev-1 focus:text-sev-1"
                disabled={context.isOrgHome === true}
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        empty={<EmptyState variant="no-data" title="No contexts found" />}
      />

      <AppSheet
        open={isCreateModalActive}
        onOpenChange={(open) => { if (!open) setIsCreateModalActive(false); }}
        title={formCloneFromId !== NO_CLONE ? 'Clone context' : 'Create new context'}
        size="md"
      >
        <AppSheet.Body>
          <form id="create-context-form" onSubmit={handleCreateContext} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="cc-name">Name</Label>
              <Input
                id="cc-name"
                type="text"
                placeholder="Context name"
                value={formName}
                onChange={(e) => { setFormName(e.target.value); }}
                required
              />
              <p className="text-xs text-muted-foreground">
                A descriptive name for this permission context.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cc-level">Level</Label>
              <Select
                value={formLevel}
                onValueChange={(v) => {
                  setFormLevel(v as 'organization' | 'division');
                }}
              >
                <SelectTrigger id="cc-level">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="organization">Organization</SelectItem>
                  <SelectItem value="division">Division</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Whether this context applies to the entire organization or a specific
                division.
              </p>
            </div>

            {formLevel === 'division' ? (
              <div className="space-y-1.5">
                <Label htmlFor="cc-division">Division</Label>
                <Select value={formDivisionId} onValueChange={setFormDivisionId} required>
                  <SelectTrigger id="cc-division">
                    <SelectValue placeholder="Select a division…" />
                  </SelectTrigger>
                  <SelectContent>
                    {divisions?.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="cc-clone">Clone from (optional)</Label>
              <Select value={formCloneFromId} onValueChange={setFormCloneFromId}>
                <SelectTrigger id="cc-clone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CLONE}>None — start with empty permissions</SelectItem>
                  {contexts?.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} ({getDivisionName(c.divisionId)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Copy permissions from an existing context.
              </p>
            </div>

            {submitError ? (
              <Alert variant="destructive">
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            type="button"
            onClick={() => { setIsCreateModalActive(false); }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-context-form"
            disabled={createContextMutation.isPending}
          >
            {createContextMutation.isPending
              ? 'Saving…'
              : formCloneFromId !== NO_CLONE
                ? 'Clone context'
                : 'Create context'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <AppSheet
        open={isEditModalActive}
        onOpenChange={(open) => { if (!open) setIsEditModalActive(false); }}
        title={`Edit context: ${editingContext?.name ?? ''}`}
        size="md"
      >
        <AppSheet.Body>
          <form id="edit-context-form" onSubmit={handleUpdateContext} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ec-name">Name</Label>
              <Input
                id="ec-name"
                type="text"
                placeholder="Context name"
                value={editFormName}
                onChange={(e) => { setEditFormName(e.target.value); }}
                required
              />
            </div>
            {submitError ? (
              <Alert variant="destructive">
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            type="button"
            onClick={() => { setIsEditModalActive(false); }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-context-form"
            disabled={updateContextMutation.isPending}
          >
            {updateContextMutation.isPending ? 'Saving…' : 'Update context'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <ConfirmationModal
        isActive={isDeleteConfirmActive}
        title="Delete context"
        message={`Are you sure you want to delete the context "${contextToDelete?.name || 'Unknown'}"? This action cannot be undone and may affect users and permissions associated with this context.`}
        confirmText="Delete context"
        cancelText="Cancel"
        onConfirm={confirmDeleteContext}
        onCancel={cancelDeleteContext}
        isLoading={deleteContextMutation.isPending}
        isDanger={true}
      />

      <Dialog
        open={isManageTeamsModalActive}
        onOpenChange={(open) => { if (!open) setIsManageTeamsModalActive(false); }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Manage teams &amp; roles: {managingContext?.name}
            </DialogTitle>
          </DialogHeader>

          {submitError ? (
            <Alert variant="destructive" className="relative">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{submitError}</AlertDescription>
              <button
                type="button"
                onClick={() => { setSubmitError(null); }}
                className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
                aria-label="Dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </Alert>
          ) : null}

          <div className="rounded-md border border-border-subtle bg-muted/30 p-3">
            <p className="text-sm font-semibold mb-2">Assign role to team</p>
            <form
              onSubmit={handleAssignTeamRole}
              className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-end"
            >
              <div className="space-y-1.5">
                <Label htmlFor="mt-team" className="text-xs">Team</Label>
                <Select value={selectedTeamId} onValueChange={setSelectedTeamId} required>
                  <SelectTrigger id="mt-team">
                    <SelectValue placeholder="Select a team…" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams?.map((team) => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.divisionName
                          ? `${team.name} (${team.divisionName})`
                          : `${team.name} (org)`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mt-role" className="text-xs">Role</Label>
                <Select value={selectedRoleId} onValueChange={setSelectedRoleId} required>
                  <SelectTrigger id="mt-role">
                    <SelectValue placeholder="Select a role…" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles?.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={assignTeamRoleMutation.isPending}>
                {assignTeamRoleMutation.isPending ? 'Assigning…' : 'Assign role'}
              </Button>
            </form>
          </div>

          <Separator />

          <div>
            <p className="text-sm font-semibold mb-2">Current team-role assignments</p>
            {isContextTeamsLoading ? (
              <p className="text-sm text-muted-foreground">Loading team assignments…</p>
            ) : contextTeams && contextTeams.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-border-subtle">
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="px-3 py-2 font-medium">Team</th>
                      <th className="px-3 py-2 font-medium">Roles</th>
                      <th className="px-3 py-2 font-medium" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-subtle">
                    {contextTeams.map((teamWithRoles) => (
                      <tr key={teamWithRoles.teamId}>
                        <td className="px-3 py-2 font-medium">
                          {formatTeamWithRolesName(teamWithRoles)}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {teamWithRoles.roles.map((role) => (
                              <Badge
                                key={role.id}
                                className="bg-sev-5/15 text-sev-5 border-0 font-normal"
                              >
                                {role.name}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {teamWithRoles.roles.map((role) => (
                              <Button
                                key={role.id}
                                variant="outline"
                                size="sm"
                                className="h-7 text-sev-1 hover:text-sev-1"
                                onClick={() => {
                                  handleRemoveTeamRole(teamWithRoles.teamId, role.id);
                                }}
                                disabled={removeTeamRoleMutation.isPending}
                              >
                                Remove {role.name}
                              </Button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No teams have been assigned roles in this context yet.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
